import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { format, formatDistanceToNowStrict } from "date-fns";

function formatRelativeTime(iso: string): string {
  try {
    return `${formatDistanceToNowStrict(new Date(iso))} ago`;
  } catch {
    return "—";
  }
}
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  ArrowLeft,
  Check,
  X,
  RefreshCw,
  Users,
  Inbox,
  Trash2,
  KeyRound,
  Eye,
  EyeOff,
  ExternalLink,
  Save,
  CalendarIcon,
  Lock,
  Trophy,
  Sparkles,
  AlertTriangle,
  Mail,
  UserPlus,
  ChevronRight,
  Settings as SettingsIcon,
  ClipboardList,
} from "lucide-react";
import { AddUserToLeagueDialog } from "@/components/AddUserToLeagueDialog";
import { DenyMembershipDialog } from "@/components/DenyMembershipDialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatLabel } from "@/lib/tournament-formats";
import { toast } from "sonner";
import { generateLeagueCode, validateLeagueCode, LEAGUE_CODE_FORMAT_HINT } from "@/lib/league";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LeagueDisplaySettings } from "@/components/LeagueDisplaySettings";
import { AdminReminderCompose } from "@/components/AdminReminderCompose";
import { LeagueAuditLog } from "@/components/LeagueAuditLog";
import { AdminGroupReminderCompose } from "@/components/AdminGroupReminderCompose";
import {
  COMMON_TIMEZONES,
  composeZonedISO,
  formatInTimezone,
  getBrowserTimezone,
  getZonedParts,
} from "@/lib/timezones";
import {
  getAdminLeagueDetail,
  updateLeagueCode,
  updateLeague,
  approveMembership,
  denyMembership,
  approvePendingSignup,
  denyPendingSignup,
  adminResetUserPassword,
  updateLeagueScoringConfig,
  updateBonusQuestion,
  verifyIsAdmin,
  deleteLeague,
  adminSelfJoinLeague,
  recomputeLeagueEngagement,
} from "@/lib/admin.functions";
import { removeMemberFromLeague } from "@/lib/league-profile.functions";
import { UserCog } from "lucide-react";

export const Route = createFileRoute("/_app/admin/leagues/$leagueId")({
  component: LeagueDetailPage,
  head: () => ({ meta: [{ title: "Manage league — Admin" }] }),
});

type Detail = Awaited<ReturnType<typeof getAdminLeagueDetail>>;

function tournamentLabel(type: string | null | undefined): string {
  return formatLabel(type);
}

function LeagueDetailPage() {
  const { leagueId } = Route.useParams();
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Server-verified admin status — null = checking.
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);

  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);
  const detailFn = useAuthedServerFn(getAdminLeagueDetail);
  const updateCodeFn = useAuthedServerFn(updateLeagueCode);
  const updateLeagueFn = useAuthedServerFn(updateLeague);
  const removeMemberFn = useAuthedServerFn(removeMemberFromLeague);
  const approveMembershipFn = useAuthedServerFn(approveMembership);
  const denyMembershipFn = useAuthedServerFn(denyMembership);
  const approveSignupFn = useAuthedServerFn(approvePendingSignup);
  const denySignupFn = useAuthedServerFn(denyPendingSignup);
  const resetPwFn = useAuthedServerFn(adminResetUserPassword);
  const updateScoringFn = useAuthedServerFn(updateLeagueScoringConfig);
  const updateBonusFn = useAuthedServerFn(updateBonusQuestion);
  const deleteLeagueFn = useAuthedServerFn(deleteLeague);
  const selfJoinFn = useAuthedServerFn(adminSelfJoinLeague);
  const recomputeEngagementFn = useAuthedServerFn(recomputeLeagueEngagement);
  const [recomputing, setRecomputing] = useState(false);

  // Danger-zone state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [deleteAck, setDeleteAck] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);

  // Password-reset confirmation
  const [resetTarget, setResetTarget] = useState<{
    userId: string;
    email: string;
    name: string;
  } | null>(null);

  // Remove-from-league dialog (reason required)
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    label: string;
  } | null>(null);
  const [removeReason, setRemoveReason] = useState("");
  const [removeAck, setRemoveAck] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Override-mode confirmation dialog (Gate 1 — friction before acting as user)
  const [overrideTarget, setOverrideTarget] = useState<{
    userId: string;
    label: string;
  } | null>(null);
  const [overrideAck, setOverrideAck] = useState(false);
  // Session-only: when ON, the members table reveals each member's real
  // first/last name instead of their per-league display name. Reset on
  // every page load — never persisted, never sent to other users.
  const [revealRealNames, setRevealRealNames] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const res = await detailFn({ data: { leagueId } });
      setDetail(res);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Failed to load league";
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (cancelled) return;
        setServerIsAdmin(res.isAdmin);
        if (res.isAdmin) await load();
      } catch {
        if (!cancelled) setServerIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, leagueId]);

  const wrap = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e: unknown) {
      const m = e instanceof Error ? e.message : "Something went wrong";
      toast.error(m);
    }
  };

  if (loading || (session && serverIsAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }

  if (!serverIsAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">
          Admins only
        </h1>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }

  if (!detail) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-12 text-center text-muted-foreground">
        Loading league…
      </main>
    );
  }

  const { league, members, pendingSignups, scoringConfig, bonusQuestions } = detail;
  const pendingCount = members.pending.length + pendingSignups.length;

  return (
    <main className="max-w-4xl mx-auto px-4 py-8">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-2 flex-wrap">
              {league.name}
              {league.is_hidden && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                  Hidden
                </span>
              )}
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                {tournamentLabel(league.tournament_type)}
              </span>
              {league.max_entries_per_member > 1 && (
                <span
                  className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold border border-primary/20"
                  title={`Members can create up to ${league.max_entries_per_member} entries`}
                >
                  Multi-entry · {league.max_entries_per_member} max
                </span>
              )}
            </h1>
            <div className="text-xs text-muted-foreground mt-1">
              /{league.slug} · code:{" "}
              <span className="font-mono text-primary">{league.code}</span>
            </div>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/members" search={{ leagueId: league.id }}>
                <Users className="w-3.5 h-3.5" /> View members
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/$league" params={{ league: league.slug }}>
                <ExternalLink className="w-3.5 h-3.5" /> Open league
              </Link>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={refreshing}
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
      </header>

      {/* League settings */}
      <section className="mb-4">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Settings
              </h2>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            <LeagueSettings
              league={league}
              onSaveName={(name) =>
                wrap(
                  () => updateLeagueFn({ data: { leagueId: league.id, name } }),
                  "Name updated",
                )
              }
              onToggleHidden={(isHidden) =>
                wrap(
                  () =>
                    updateLeagueFn({
                      data: { leagueId: league.id, isHidden },
                    }),
                  isHidden ? "League hidden" : "League visible",
                )
              }
              onRotateCode={(newCode) =>
                wrap(
                  () => updateCodeFn({ data: { leagueId: league.id, newCode } }),
                  "Code updated",
                )
              }
              onSaveLockAt={(iso, tz, immediate) =>
                wrap(
                  () =>
                    updateLeagueFn({
                      data: {
                        leagueId: league.id,
                        bracketLockAt: iso,
                        bracketLockTimezone: tz,
                        immediateLock: immediate || undefined,
                      },
                    }),
                  iso ? "Lockdown set" : "Lockdown cleared",
                )
              }
              onSaveGroupLockAt={(iso, tz, immediate) =>
                wrap(
                  () =>
                    updateLeagueFn({
                      data: {
                        leagueId: league.id,
                        groupStageLockAt: iso,
                        bracketLockTimezone: tz,
                        immediateLock: immediate || undefined,
                      },
                    }),
                  iso ? "Group lockdown set" : "Group lockdown cleared",
                )
              }
              onSaveMaxEntries={(maxEntriesPerMember) =>
                wrap(
                  () =>
                    updateLeagueFn({
                      data: { leagueId: league.id, maxEntriesPerMember },
                    }),
                  "Max entries updated",
                )
              }
              onToggleAutoApprove={(autoApproveJoinByCode) =>
                wrap(
                  () =>
                    updateLeagueFn({
                      data: { leagueId: league.id, autoApproveJoinByCode },
                    }),
                  autoApproveJoinByCode ? "Auto-approve ON" : "Auto-approve OFF",
                )
              }
            />
          </div>
        </details>
      </section>

      {/* Scoring config */}
      {scoringConfig && (
        <section className="mb-4">
          <details className="group rounded-xl border border-border bg-card">
            <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center gap-2 flex-wrap">
                <Trophy className="w-4 h-4 text-primary" />
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Scoring
                </h2>
                {scoringConfig.is_locked && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-semibold inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Locked
                  </span>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </summary>
            <div className="px-4 pb-4">
              <ScoringConfigCard
                config={scoringConfig}
                leagueName={league.name}
                tournamentType={league.tournament_type}
                enabledBonusCount={bonusQuestions.filter((q) => q.is_enabled).length}
                onSave={(values) =>
                  wrap(
                    () =>
                      updateScoringFn({
                        data: { leagueId: league.id, ...values },
                      }),
                    "Scoring updated",
                  )
                }
              />
            </div>
          </details>
        </section>
      )}

      {/* Bonus questions */}
      {bonusQuestions.length > 0 && (
        <section className="mb-4">
          <details className="group rounded-xl border border-border bg-card">
            <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center gap-2 flex-wrap">
                <Sparkles className="w-4 h-4 text-primary" />
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Bonus Questions
                </h2>
                <span className="text-xs text-muted-foreground font-normal">
                  ({scoringConfig?.points_per_bonus_question ?? 1} pt each)
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </summary>
            <div className="px-4 pb-4">
              <BonusQuestionsCard
                questions={bonusQuestions}
                isLocked={scoringConfig?.is_locked ?? false}
                onUpdate={(id, patch) =>
                  wrap(
                    () =>
                      updateBonusFn({
                        data: { bonusQuestionId: id, ...patch },
                      }),
                    "Updated",
                  )
                }
              />
            </div>
          </details>
        </section>
      )}

      {/* Reminder emails */}
      <section className="mb-4">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Bracket reminder emails
              </h2>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            <AdminReminderCompose leagueId={league.id} />
          </div>
        </details>
      </section>

      {league.tournament_type === "fifa_wc_mens_group_knockout" && (
        <section className="mb-4">
          <details className="group rounded-xl border border-border bg-card">
            <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-primary" />
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Group-stage reminder emails
                </h2>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </summary>
            <div className="px-4 pb-4">
              <AdminGroupReminderCompose leagueId={league.id} />
            </div>
          </details>
        </section>
      )}

      {/* Pending */}
      <section className="mb-4">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Pending requests
              </h2>
              {pendingCount > 0 && (
                <span className="text-primary text-sm font-mono">
                  ({pendingCount})
                </span>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            {pendingCount === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                No pending requests for this league.
              </div>
            ) : (
              <div className="space-y-2">
                {pendingSignups.map((s) => (
                  <div
                    key={`ps-${s.id}`}
                    className="rounded-xl border border-border bg-background p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">
                          New signup
                        </span>
                        <div className="font-semibold text-foreground truncate">
                          {s.first_name} {s.last_name}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {s.email}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() =>
                          wrap(
                            () =>
                              approveSignupFn({
                                data: { pendingSignupId: s.id },
                              }),
                            "Approved & invite sent",
                          )
                        }
                      >
                        <Check className="w-4 h-4" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          wrap(
                            () =>
                              denySignupFn({
                                data: { pendingSignupId: s.id },
                              }),
                            "Denied",
                          )
                        }
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}

                {members.pending.map((m) => {
                  const attempts = (m as { attempt_count?: number }).attempt_count ?? 1;
                  const priorReason = (m as { denial_reason?: string | null }).denial_reason ?? null;
                  const label = `${m.profile?.first_name ?? ""} ${m.profile?.last_name ?? ""}`.trim() || (m.profile?.email ?? "this user");
                  return (
                  <div
                    key={`mem-${m.id}`}
                    className="rounded-xl border border-border bg-background p-4 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                          Existing user
                        </span>
                        <div className="font-semibold text-foreground truncate">
                          {m.profile?.first_name} {m.profile?.last_name}
                        </div>
                        {attempts > 1 && (
                          <span
                            title={priorReason ? `Prior denial reason: "${priorReason}"` : "Previously denied (no reason given)"}
                            className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold"
                          >
                            Attempt #{attempts}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {m.profile?.email}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        onClick={() =>
                          wrap(
                            () =>
                              approveMembershipFn({
                                data: { membershipId: m.id },
                              }),
                            "Approved",
                          )
                        }
                      >
                        <Check className="w-4 h-4" /> Approve
                      </Button>
                      <DenyMembershipDialog
                        userLabel={label}
                        attemptCount={attempts}
                        priorReason={priorReason}
                        onConfirm={(reason) =>
                          wrap(
                            () =>
                              denyMembershipFn({
                                data: { membershipId: m.id, reason },
                              }),
                            "Denied",
                          )
                        }
                      />
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </details>
      </section>

      {/* Members */}
      <section className="mb-4">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Members
              </h2>
              <span className="text-sm text-muted-foreground font-mono">
                ({members.approved.length})
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <Switch
                  checked={revealRealNames}
                  onCheckedChange={setRevealRealNames}
                  aria-label="Reveal real names"
                />
                <span>
                  Reveal real names{" "}
                  <span className="text-muted-foreground/70">(this session only)</span>
                </span>
              </label>
              <div className="flex flex-wrap items-center gap-2">
                {session?.user?.id &&
                  !members.approved.some((m) => m.user_id === session.user.id) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const res = await selfJoinFn({ data: { leagueId } });
                          if (res.membershipStatus === "already_member") {
                            toast.info("You're already a member.");
                          } else {
                            const verb = res.membershipStatus === "reapproved" ? "Rejoined" : "Joined";
                            toast.success(`${verb} this league — name your bracket next.`, {
                              action: {
                                label: "Create entry",
                                onClick: () =>
                                  navigate({
                                    to: "/$league/entries/new",
                                    params: { league: league.slug },
                                  }),
                              },
                              duration: 8000,
                            });
                          }
                          await load();
                        } catch (e: unknown) {
                          const msg = e instanceof Error ? e.message : "Failed to join league";
                          toast.error(msg);
                        }
                      }}
                    >
                      <UserPlus className="w-3.5 h-3.5" /> Join this league
                    </Button>
                  )}
                <Button size="sm" variant="outline" onClick={() => setAddUserOpen(true)}>
                  <UserPlus className="w-3.5 h-3.5" /> Add existing user
                </Button>
              </div>
            </div>

            {members.approved.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                No members yet.
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border bg-background">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="px-4 py-2.5 font-semibold">Name</th>
                      <th className="px-4 py-2.5 font-semibold">Email</th>
                      <th className="px-4 py-2.5 font-semibold">Joined</th>
                      <th className="px-4 py-2.5 font-semibold">Last login</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {members.approved.map((m) => (
                      <tr
                        key={m.id}
                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-semibold text-foreground">
                          <div className="flex items-center gap-2 flex-wrap">
                            {(() => {
                              const realName = `${m.profile?.first_name ?? ""} ${m.profile?.last_name ?? ""}`.trim();
                              const displayName = m.profile?.display_name?.trim() || "";
                              const primary = revealRealNames
                                ? (realName || displayName || "—")
                                : (displayName || realName || "—");
                              const secondary =
                                revealRealNames && displayName && displayName !== realName
                                  ? `aka ${displayName}`
                                  : null;
                              return (
                                <span>
                                  <span>{primary}</span>
                                  {secondary && (
                                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                      {secondary}
                                    </span>
                                  )}
                                </span>
                              );
                            })()}
                            {league.max_entries_per_member > 1 && (
                              <span
                                className="inline-flex items-center rounded-full border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground"
                                title={`${m.entryCount} of ${league.max_entries_per_member} entries`}
                              >
                                {m.entryCount}/{league.max_entries_per_member} entries
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {m.profile?.email ?? "—"}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {m.decided_at
                            ? new Date(m.decided_at).toLocaleDateString()
                            : "—"}
                        </td>
                        <td
                          className="px-4 py-3 text-xs text-muted-foreground"
                          title={
                            m.profile?.last_sign_in_at
                              ? new Date(m.profile.last_sign_in_at).toLocaleString()
                              : "Never signed in"
                          }
                        >
                          {m.profile?.last_sign_in_at
                            ? formatRelativeTime(m.profile.last_sign_in_at)
                            : "Never"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <LeagueDisplaySettings
                              leagueId={league.id}
                              leagueName={league.name}
                              targetUserId={m.user_id}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Edit this member's picks (override mode)"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-500/10"
                              onClick={() => {
                                const label =
                                  m.profile?.display_name ||
                                  `${m.profile?.first_name ?? ""} ${m.profile?.last_name ?? ""}`.trim() ||
                                  m.profile?.email ||
                                  "this user";
                                setOverrideTarget({ userId: m.user_id, label });
                                setOverrideAck(false);
                              }}
                            >
                              <UserCog className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Send password reset"
                              onClick={() => {
                                const name =
                                  m.profile?.display_name ||
                                  `${m.profile?.first_name ?? ""} ${m.profile?.last_name ?? ""}`.trim() ||
                                  m.profile?.email ||
                                  "this user";
                                const email = m.profile?.email ?? "";
                                if (!email) {
                                  toast.error("No email on file for this user");
                                  return;
                                }
                                setResetTarget({ userId: m.user_id, email, name });
                              }}
                            >
                              <KeyRound className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Remove from league"
                              onClick={() => {
                                const label =
                                  m.profile?.display_name ||
                                  `${m.profile?.first_name ?? ""} ${m.profile?.last_name ?? ""}`.trim() ||
                                  m.profile?.email ||
                                  "this user";
                                setRemoveTarget({ userId: m.user_id, label });
                                setRemoveReason("");
                                setRemoveAck(false);
                              }}
                            >
                              <Trash2 className="w-3.5 h-3.5 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {members.denied.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                  {members.denied.length} denied
                </summary>
                <div className="mt-2 space-y-1">
                  {members.denied.map((m) => (
                    <div
                      key={m.id}
                      className="rounded-lg border border-border bg-background p-2 text-xs text-muted-foreground flex justify-between"
                    >
                      <span>
                        {m.profile?.email ?? m.user_id}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </details>
      </section>

      {/* Recompute engagement (Hot Take badges + Bracket Twins) */}
      <section className="mb-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-primary mt-0.5" />
              <div>
                <h2 className="font-display text-lg font-semibold text-foreground">
                  Engagement (badges &amp; twins)
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Re-runs Hot Take badge + Bracket Twins compute. Auto-fires on
                  first bracket lock; use this to refresh after manual fixes.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={recomputing}
              onClick={async () => {
                setRecomputing(true);
                try {
                  const r = await recomputeEngagementFn({
                    data: { leagueId: league.id, emitFeedEvents: false },
                  });
                  toast.success(
                    `Recomputed: ${r.badgesAwarded} badges, ${r.insightsWritten} insights`,
                  );
                } catch (e) {
                  toast.error((e as Error).message);
                } finally {
                  setRecomputing(false);
                }
              }}
            >
              <RefreshCw
                className={cn(
                  "w-3.5 h-3.5 mr-1.5",
                  recomputing && "animate-spin",
                )}
              />
              {recomputing ? "Recomputing…" : "Recompute"}
            </Button>
          </div>
        </div>
      </section>

      {/* Audit log */}
      <section className="mb-4">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Audit log
              </h2>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            <LeagueAuditLog leagueId={league.id} />
          </div>
        </details>
      </section>

      {/* Danger zone */}
      {(() => {
        const isInProgress =
          league.bracket_lock_at !== null &&
          new Date(league.bracket_lock_at) <= new Date();
        const nameMatches = deleteName.trim() === league.name;
        const canDelete = nameMatches && (!isInProgress || deleteAck) && !deleting;

        const handleDelete = async () => {
          if (!canDelete) return;
          setDeleting(true);
          try {
            await deleteLeagueFn({
              data: {
                leagueId: league.id,
                nameConfirmation: deleteName,
                acknowledgeInProgress: deleteAck,
              },
            });
            toast.success(`${league.name} deleted.`);
            navigate({ to: "/admin" });
          } catch (e: unknown) {
            const m = e instanceof Error ? e.message : "Failed to delete league.";
            toast.error(m);
            setDeleting(false);
          }
        };

        return (
          <>
            <section className="mt-10 rounded-xl border border-destructive/30 bg-destructive/5 p-5">
              <h2 className="font-display font-semibold text-destructive flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Danger zone
              </h2>
              <p className="mt-2 text-xs text-muted-foreground">
                Deleting this league permanently removes all{" "}
                <span className="font-mono text-foreground">
                  {members.approved.length}
                </span>{" "}
                memberships, every bracket pick, and every bonus answer for{" "}
                <span className="font-semibold text-foreground">{league.name}</span>.
                Members keep their accounts and can join a different league with a
                code. This can't be undone.
              </p>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setDeleteName("");
                  setDeleteAck(false);
                  setDeleteOpen(true);
                }}
              >
                <Trash2 className="w-4 h-4" /> Delete this league
              </Button>
            </section>

            <Dialog
              open={deleteOpen}
              onOpenChange={(open) => {
                if (deleting) return;
                setDeleteOpen(open);
                if (!open) {
                  setDeleteName("");
                  setDeleteAck(false);
                }
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete {league.name}?</DialogTitle>
                  <DialogDescription>
                    This permanently deletes the league and removes{" "}
                    <span className="font-mono">{members.approved.length}</span>{" "}
                    member{members.approved.length === 1 ? "" : "s"}' brackets and
                    bonus answers. Member accounts are preserved.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-league-name">
                      Type{" "}
                      <span className="font-mono text-foreground">{league.name}</span>{" "}
                      to confirm:
                    </Label>
                    <Input
                      id="confirm-league-name"
                      autoComplete="off"
                      value={deleteName}
                      onChange={(e) => setDeleteName(e.target.value)}
                      placeholder={league.name}
                      disabled={deleting}
                    />
                  </div>
                  {isInProgress && (
                    <label className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-foreground cursor-pointer">
                      <Checkbox
                        checked={deleteAck}
                        onCheckedChange={(v) => setDeleteAck(v === true)}
                        disabled={deleting}
                        className="mt-0.5"
                      />
                      <span>
                        <span className="font-semibold text-destructive">
                          This tournament is in progress.
                        </span>{" "}
                        I understand bracket lockdown has passed and members have
                        live picks that will be permanently lost.
                      </span>
                    </label>
                  )}
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteOpen(false)}
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={!canDelete}
                  >
                    {deleting ? "Deleting…" : "Permanently delete league"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        );
      })()}

      <AddUserToLeagueDialog
        open={addUserOpen}
        onOpenChange={setAddUserOpen}
        mode="pickUser"
        fixedLeague={{
          id: league.id,
          name: league.name,
          slug: league.slug,
          code: league.code,
          bracket_lock_at: league.bracket_lock_at,
          tournament_type: league.tournament_type,
          is_hidden: league.is_hidden,
        }}
        onSuccess={load}
      />

      <EmailConfirmDialog
        open={resetTarget !== null}
        onOpenChange={(open) => {
          if (!open) setResetTarget(null);
        }}
        title="Send password reset email?"
        description="The recipient will get an email with a link to set a new password."
        recipients={
          resetTarget ? [`${resetTarget.name} — ${resetTarget.email}`] : undefined
        }
        confirmLabel="Send reset email"
        onConfirm={async () => {
          if (!resetTarget) return;
          try {
            await resetPwFn({ data: { userId: resetTarget.userId } });
            toast.success("Reset email sent");
            await load();
          } catch (e) {
            toast.error(e instanceof Error ? e.message : "Failed to send reset");
            throw e;
          }
        }}
      />

      {/* Gate 1: confirm before entering override (act-as) mode */}
      <Dialog
        open={overrideTarget !== null}
        onOpenChange={(open) => {
          if (!open) setOverrideTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <UserCog className="w-5 h-5" />
              Act as {overrideTarget?.label}?
            </DialogTitle>
            <DialogDescription>
              You are about to enter <strong>override mode</strong> and view this
              league as <strong>{overrideTarget?.label}</strong>. Any picks you
              save will be written to <em>their</em> account and recorded in the
              admin audit log. Post-lock saves additionally require a written
              reason.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-300 space-y-1">
            <p>While in override mode you'll see a persistent amber banner at the top of every league page.</p>
            <p>Exit override at any time by clicking "Exit override" in that banner.</p>
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={overrideAck}
              onCheckedChange={(v) => setOverrideAck(v === true)}
            />
            <span>
              I understand I will be acting as {overrideTarget?.label} and any
              changes are attributed to them in the audit log.
            </span>
          </label>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOverrideTarget(null)}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 text-white hover:bg-amber-700"
              disabled={!overrideAck}
              onClick={() => {
                if (!overrideTarget) return;
                const target = overrideTarget;
                setOverrideTarget(null);
                navigate({
                  to: "/$league",
                  params: { league: league.slug },
                  search: { actAs: target.userId },
                });
              }}
            >
              Enter override mode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove member from league (reason required) */}
      <Dialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !removing) setRemoveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Remove {removeTarget?.label} from {league.name}?
            </DialogTitle>
            <DialogDescription>
              This sets their membership status to <strong>removed</strong>. Their
              picks and history are preserved for the audit trail. They will no
              longer see this league in their list and won't be able to make or
              change picks. They can request to rejoin later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="remove-reason" className="text-xs">
                Reason (required, captured in audit log)
              </Label>
              <textarea
                id="remove-reason"
                value={removeReason}
                onChange={(e) => setRemoveReason(e.target.value)}
                placeholder="e.g. Joined wrong league, requested removal, abandoned bracket…"
                rows={3}
                maxLength={500}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                checked={removeAck}
                onCheckedChange={(v) => setRemoveAck(v === true)}
              />
              <span>
                I understand this will remove this member from the league.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRemoveTarget(null)}
              disabled={removing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                !removeAck || removeReason.trim().length === 0 || removing
              }
              onClick={async () => {
                if (!removeTarget) return;
                setRemoving(true);
                try {
                  await removeMemberFn({
                    data: {
                      leagueId: league.id,
                      targetUserId: removeTarget.userId,
                      reason: removeReason.trim(),
                    },
                  });
                  toast.success("Member removed");
                  setRemoveTarget(null);
                  await load();
                } catch (e) {
                  toast.error(
                    e instanceof Error ? e.message : "Failed to remove member",
                  );
                } finally {
                  setRemoving(false);
                }
              }}
            >
              {removing ? "Removing…" : "Remove member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function LeagueSettings({
  league,
  onSaveName,
  onToggleHidden,
  onRotateCode,
  onSaveLockAt,
  onSaveGroupLockAt,
  onSaveMaxEntries,
  onToggleAutoApprove,
}: {
  league: {
    id: string;
    name: string;
    slug: string;
    code: string;
    is_hidden: boolean;
    bracket_lock_at: string | null;
    bracket_lock_timezone: string | null;
    group_stage_lock_at: string | null;
    tournament_type: string;
    max_entries_per_member: number;
    auto_approve_join_by_code?: boolean;
  };
  onSaveName: (name: string) => Promise<void>;
  onToggleHidden: (isHidden: boolean) => Promise<void>;
  onRotateCode: (newCode: string) => Promise<void>;
  onSaveLockAt: (iso: string | null, timezone: string, immediate?: boolean) => Promise<void>;
  onSaveGroupLockAt: (iso: string | null, timezone: string, immediate?: boolean) => Promise<void>;
  onSaveMaxEntries: (maxEntriesPerMember: number) => Promise<void>;
  onToggleAutoApprove: (autoApproveJoinByCode: boolean) => Promise<void>;
}) {
  const [name, setName] = useState(league.name);
  const [code, setCode] = useState(league.code);
  const [maxEntries, setMaxEntries] = useState<number>(league.max_entries_per_member);
  const nameDirty = name.trim() !== league.name;
  const codeDirty = code.trim().toUpperCase() !== league.code;
  const maxEntriesDirty =
    maxEntries !== league.max_entries_per_member && maxEntries >= 1 && maxEntries <= 20;

  const isGroupFormat = league.tournament_type === "fifa_wc_mens_group_knockout";
  const savedTz = league.bracket_lock_timezone || "UTC";
  const [lockTz, setLockTz] = useState<string>(
    league.bracket_lock_at || league.group_stage_lock_at ? savedTz : getBrowserTimezone(),
  );

  // ----- Bracket lockdown state -----
  const initialParts = league.bracket_lock_at
    ? getZonedParts(league.bracket_lock_at, savedTz)
    : null;
  const [lockDate, setLockDate] = useState<Date | undefined>(
    initialParts
      ? new Date(initialParts.year, initialParts.month - 1, initialParts.day)
      : undefined,
  );
  const [lockTime, setLockTime] = useState<string>(
    initialParts ? `${initialParts.hour}:${initialParts.minute}` : "15:00",
  );
  const lockedNow = league.bracket_lock_at && new Date(league.bracket_lock_at) <= new Date();
  const composedISO = lockDate ? composeZonedISO(lockDate, lockTime, lockTz) : null;
  const lockDirty =
    composedISO !== league.bracket_lock_at || (composedISO !== null && lockTz !== savedTz);
  const previewDisplay = composedISO ? formatInTimezone(composedISO, lockTz) : null;

  // ----- Group-stage lockdown state (only used when isGroupFormat) -----
  const groupInitialParts = league.group_stage_lock_at
    ? getZonedParts(league.group_stage_lock_at, savedTz)
    : null;
  const [groupLockDate, setGroupLockDate] = useState<Date | undefined>(
    groupInitialParts
      ? new Date(groupInitialParts.year, groupInitialParts.month - 1, groupInitialParts.day)
      : undefined,
  );
  const [groupLockTime, setGroupLockTime] = useState<string>(
    groupInitialParts ? `${groupInitialParts.hour}:${groupInitialParts.minute}` : "15:00",
  );
  const groupLockedNow =
    league.group_stage_lock_at && new Date(league.group_stage_lock_at) <= new Date();
  const groupComposedISO = groupLockDate
    ? composeZonedISO(groupLockDate, groupLockTime, lockTz)
    : null;
  const groupLockDirty =
    groupComposedISO !== league.group_stage_lock_at ||
    (groupComposedISO !== null && lockTz !== savedTz);
  const groupPreviewDisplay = groupComposedISO ? formatInTimezone(groupComposedISO, lockTz) : null;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-baseline justify-between">
            <Label>Name</Label>
            <span className={`text-xs tabular-nums ${name.length > 60 ? "text-destructive" : "text-muted-foreground"}`}>
              {name.length}/60
            </span>
          </div>
          <div className="flex gap-2">
            <Input maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
            {nameDirty && (
              <Button size="sm" onClick={() => onSaveName(name.trim())}>
                <Save className="w-4 h-4" />
              </Button>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Up to 100 characters.</p>
        </div>
        <div>
          <Label>Slug</Label>
          <Input value={league.slug} disabled className="text-muted-foreground" />
        </div>
      </div>

      <div>
        <Label>Join code</Label>
        <div className="flex gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="font-mono"
            aria-invalid={code.trim().length > 0 && !validateLeagueCode(code).ok}
            maxLength={32}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            title="Generate new code"
            onClick={() => setCode(generateLeagueCode(league.name))}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {codeDirty && (
            <Button
              size="sm"
              onClick={() => onRotateCode(code.trim())}
              disabled={!validateLeagueCode(code).ok}
            >
              <Save className="w-4 h-4" />
            </Button>
          )}
        </div>
        {(() => {
          const v = validateLeagueCode(code);
          return v.ok ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Members use this code to request access. {LEAGUE_CODE_FORMAT_HINT}
            </p>
          ) : (
            <p className="mt-1 text-xs text-destructive">{v.error}</p>
          );
        })()}
      </div>

      <div className="pt-2 border-t border-border">
        <Label>Max entries per member</Label>
        <div className="flex items-center gap-2 mt-1">
          <Input
            type="number"
            min={1}
            max={20}
            step={1}
            value={maxEntries}
            onChange={(e) => setMaxEntries(parseInt(e.target.value, 10) || 1)}
            className="w-32"
          />
          {maxEntriesDirty && (
            <Button size="sm" onClick={() => onSaveMaxEntries(maxEntries)}>
              <Save className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          How many separate brackets each member can submit. Default 1; raise this for leagues where members can enter brackets on behalf of kids, partners, etc. Lowering this won't delete existing entries.
        </p>
      </div>

      <div className="pt-2 border-t border-border">
        <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
          <Checkbox
            checked={league.auto_approve_join_by_code ?? false}
            onCheckedChange={(c) => onToggleAutoApprove(c === true)}
            className="mt-0.5"
          />
          <span>
            <span className="font-medium">Auto-approve join requests</span>
            <span className="block text-xs text-muted-foreground mt-0.5">
              When ON, anyone with the league code is approved instantly — no admin review needed. New users still need to set their password from the invite email before they can sign in.
            </span>
          </span>
        </label>
      </div>

      {/* Shared timezone picker — applies to BOTH group lock and bracket lock */}
      <div className="pt-2 border-t border-border">
        <Label className="flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-primary" />
          Lockdown timezone
        </Label>
        <div className="mt-1.5">
          <Select value={lockTz} onValueChange={setLockTz}>
            <SelectTrigger>
              <SelectValue placeholder="Pick a timezone" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {!COMMON_TIMEZONES.some((o) => o.value === lockTz) && (
                <SelectItem value={lockTz}>{lockTz}</SelectItem>
              )}
              {COMMON_TIMEZONES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}{" "}
                  <span className="text-muted-foreground">({o.value})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Applies to {isGroupFormat ? "both the group-stage and knockout" : "the bracket"} lock time{isGroupFormat ? "s" : ""} below. Everyone — regardless of where they live — will see the countdown end at the same real-world instant.
        </p>
      </div>

      {/* Group-stage lockdown — only for Group + Knockout format */}
      {isGroupFormat && (
        <div className="pt-2 border-t border-border">
          <Label className="flex items-center gap-1.5">
            <Lock className="w-3.5 h-3.5 text-primary" />
            Group-stage lockdown
            {groupLockedNow && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-semibold ml-1">
                Locked
              </span>
            )}
          </Label>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2 mt-1.5">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "justify-start text-left font-normal",
                    !groupLockDate && "text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="w-4 h-4" />
                  {groupLockDate ? format(groupLockDate, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={groupLockDate}
                  onSelect={setGroupLockDate}
                  disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <Input
              type="time"
              value={groupLockTime}
              onChange={(e) => setGroupLockTime(e.target.value)}
            />
          </div>
          <div className="flex gap-1 mt-2 justify-end">
            {groupLockDirty && groupComposedISO && (
              <Button size="sm" onClick={() => onSaveGroupLockAt(groupComposedISO, lockTz)}>
                <Save className="w-4 h-4" />
              </Button>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  title="Force group lock now (admin override for QA/testing)"
                >
                  Lock now
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Lock group picks now?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will immediately freeze group-stage picks for{" "}
                    <span className="font-semibold">{league.name}</span>. Members
                    will no longer be able to change their group predictions.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => onSaveGroupLockAt(new Date().toISOString(), lockTz, true)}
                  >
                    Yes, lock now
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {league.group_stage_lock_at && (
              <Button
                size="sm"
                variant="ghost"
                title="Clear group lockdown"
                onClick={() => {
                  setGroupLockDate(undefined);
                  onSaveGroupLockAt(null, lockTz);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          {groupPreviewDisplay && (
            <p className="mt-2 text-xs text-foreground">
              <span className="text-muted-foreground">Group picks lock at:</span>{" "}
              <span className="font-semibold">{groupPreviewDisplay}</span>
            </p>
          )}
          <p className="mt-1.5 text-xs text-muted-foreground">
            Freezes group-stage predictions only. The bracket (knockout) lock
            below is independent — set it after groups complete and you've
            seeded the Round of 32.
          </p>
        </div>
      )}

      {/* Bracket lockdown */}
      <div className="pt-2 border-t border-border">
        <Label className="flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-primary" />
          {isGroupFormat ? "Knockout (bracket) lockdown" : "Bracket lockdown"}
          {lockedNow && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/20 text-destructive font-semibold ml-1">
              Locked
            </span>
          )}
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-2 mt-1.5">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "justify-start text-left font-normal",
                  !lockDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="w-4 h-4" />
                {lockDate ? format(lockDate, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={lockDate}
                onSelect={setLockDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          <Input
            type="time"
            value={lockTime}
            onChange={(e) => setLockTime(e.target.value)}
          />
        </div>

        <div className="flex gap-1 mt-2 justify-end">
          {lockDirty && composedISO && (
            <Button size="sm" onClick={() => onSaveLockAt(composedISO, lockTz)}>
              <Save className="w-4 h-4" />
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                title="Force lock now (admin override for QA/testing)"
              >
                Lock now
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Lock this league now?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will immediately lock <span className="font-semibold">{league.name}</span>.
                  Members will no longer be able to make or change picks. This action is intended
                  for admin override / QA — proceed with caution.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => onSaveLockAt(new Date().toISOString(), lockTz, true)}
                >
                  Yes, lock now
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          {league.bracket_lock_at && (
            <Button
              size="sm"
              variant="ghost"
              title="Clear lockdown"
              onClick={() => {
                setLockDate(undefined);
                onSaveLockAt(null, lockTz);
              }}
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>

        {previewDisplay && (
          <p className="mt-2 text-xs text-foreground">
            <span className="text-muted-foreground">Locks at:</span>{" "}
            <span className="font-semibold">{previewDisplay}</span>
          </p>
        )}
        <p className="mt-1.5 text-xs text-muted-foreground">
          Pick the date and time members should think of as the deadline (in
          the timezone selected above). The countdown is anchored to our
          server clock, so changing your device clock won't buy extra time.
        </p>
      </div>

      <div className="pt-2 border-t border-border flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">
            {league.is_hidden ? "Hidden league" : "Visible league"}
          </div>
          <div className="text-xs text-muted-foreground">
            Hidden leagues won't show up in members' league lists.
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleHidden(!league.is_hidden)}
        >
          {league.is_hidden ? (
            <>
              <Eye className="w-4 h-4" /> Unhide
            </>
          ) : (
            <>
              <EyeOff className="w-4 h-4" /> Hide
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ============= Scoring config card =============
type ScoringConfig = {
  points_round_of_32: number;
  points_round_of_16: number;
  points_quarterfinals: number;
  points_semifinals: number;
  points_third_place: number;
  points_final: number;
  points_per_correct_goal_count: number;
  points_per_correct_group_position: number;
  points_per_bonus_question: number;
  is_locked: boolean;
};

type ScoringPatch = {
  pointsRoundOf32: number;
  pointsRoundOf16: number;
  pointsQuarterfinals: number;
  pointsSemifinals: number;
  pointsThirdPlace: number;
  pointsFinal: number;
  pointsPerCorrectGoalCount: number;
  pointsPerCorrectGroupPosition: number;
  pointsPerBonusQuestion: number;
};

const SCORING_FIELDS: Array<{
  key: keyof ScoringConfig;
  label: string;
  patchKey: keyof ScoringPatch;
  hint?: string;
  groupKnockoutOnly?: boolean;
  section: "group" | "knockout" | "extras";
}> = [
  {
    key: "points_per_correct_group_position",
    label: "Group Position (per pick)",
    patchKey: "pointsPerCorrectGroupPosition",
    hint: "per team placed correctly",
    groupKnockoutOnly: true,
    section: "group",
  },
  { key: "points_round_of_32", label: "Round of 32", patchKey: "pointsRoundOf32", section: "knockout" },
  { key: "points_round_of_16", label: "Round of 16", patchKey: "pointsRoundOf16", section: "knockout" },
  { key: "points_quarterfinals", label: "Quarterfinal", patchKey: "pointsQuarterfinals", section: "knockout" },
  { key: "points_semifinals", label: "Semifinal", patchKey: "pointsSemifinals", section: "knockout" },
  { key: "points_third_place", label: "3rd Place Game", patchKey: "pointsThirdPlace", section: "knockout" },
  { key: "points_final", label: "Final (= Champion)", patchKey: "pointsFinal", hint: "🏆", section: "knockout" },
  {
    key: "points_per_correct_goal_count",
    label: "Goal Count (per game)",
    patchKey: "pointsPerCorrectGoalCount",
    hint: "tiebreaker",
    section: "extras",
  },
  {
    key: "points_per_bonus_question",
    label: "Bonus Question (each)",
    patchKey: "pointsPerBonusQuestion",
    hint: "applies to every bonus question",
    section: "extras",
  },
];

function ScoringConfigCard({
  config,
  leagueName,
  tournamentType,
  enabledBonusCount,
  onSave,
}: {
  config: ScoringConfig;
  leagueName: string;
  tournamentType: string;
  enabledBonusCount: number;
  onSave: (values: ScoringPatch & { confirmLeagueName?: string }) => Promise<void>;
}) {
  const isGroupKnockout = tournamentType === "fifa_wc_mens_group_knockout";
  const fields = SCORING_FIELDS.filter(
    (f) => !f.groupKnockoutOnly || isGroupKnockout,
  );
  const [values, setValues] = useState<ScoringPatch>({
    pointsRoundOf32: config.points_round_of_32,
    pointsRoundOf16: config.points_round_of_16,
    pointsQuarterfinals: config.points_quarterfinals,
    pointsSemifinals: config.points_semifinals,
    pointsThirdPlace: config.points_third_place,
    pointsFinal: config.points_final,
    pointsPerCorrectGoalCount: config.points_per_correct_goal_count,
    pointsPerCorrectGroupPosition: config.points_per_correct_group_position ?? 1,
    pointsPerBonusQuestion: config.points_per_bonus_question ?? 1,
  });
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const dirty = fields.some(
    (f) => values[f.patchKey] !== (config[f.key] as number),
  );

  const diff = fields.filter(
    (f) => values[f.patchKey] !== (config[f.key] as number),
  );

  const confirmMatches =
    confirmText.trim().toLowerCase() === leagueName.trim().toLowerCase();

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      {(["group", "knockout", "extras"] as const).map((section) => {
        const sectionFields = fields.filter((f) => f.section === section);
        if (sectionFields.length === 0) return null;
        const sectionMeta = {
          group: {
            title: "Group Stage",
            desc: "Points awarded per correct team placement (1st–4th) in each group.",
            tone: "border-l-4 border-l-blue-500/60",
          },
          knockout: {
            title: "Knockout Stage",
            desc: "Points awarded per correct winner advanced to each round.",
            tone: "border-l-4 border-l-amber-500/60",
          },
          extras: {
            title: "Extras",
            desc: "Per-game goal-count bonus — also used as the final tiebreaker.",
            tone: "border-l-4 border-l-emerald-500/60",
          },
        }[section];
        return (
          <div key={section} className={`mb-4 rounded-md bg-muted/30 p-3 ${sectionMeta.tone}`}>
            <div className="mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-foreground">
                {sectionMeta.title}
              </div>
              <div className="text-xs text-muted-foreground">{sectionMeta.desc}</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {sectionFields.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs">
                    {f.label}
                    {f.hint && (
                      <span className="ml-1 text-muted-foreground font-normal">
                        {f.hint}
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={values[f.patchKey]}
                    onChange={(e) =>
                      setValues((v) => ({
                        ...v,
                        [f.patchKey]: Math.max(0, parseInt(e.target.value, 10) || 0),
                      }))
                    }
                    className="font-mono"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground">
          {config.is_locked
            ? "Locked — first match result already entered. Edits require override."
            : "Locks automatically when the first match result is entered."}
        </p>
        {dirty && !config.is_locked && (
          <Button size="sm" onClick={() => onSave(values)}>
            <Save className="w-4 h-4" /> Save scoring
          </Button>
        )}
        {dirty && config.is_locked && (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => {
              setConfirmText("");
              setOverrideOpen(true);
            }}
          >
          <AlertTriangle className="w-4 h-4" /> Override & save
          </Button>
        )}
      </div>

      {/* Possible points summary */}
      <div className="mt-4 rounded-md border border-border bg-muted/30 p-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-foreground mb-2">
          Possible Points Summary
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          {(() => {
            const p = values;
            const groupTotal = isGroupKnockout ? 48 * p.pointsPerCorrectGroupPosition : 0;
            const knockoutRoundsTotal =
              16 * p.pointsRoundOf32 +
              8 * p.pointsRoundOf16 +
              4 * p.pointsQuarterfinals +
              2 * p.pointsSemifinals +
              1 * p.pointsThirdPlace +
              1 * p.pointsFinal;
            const goalCountTotal = 32 * p.pointsPerCorrectGoalCount;
            const bonusTotal = enabledBonusCount * p.pointsPerBonusQuestion;
            const total = groupTotal + knockoutRoundsTotal + goalCountTotal + bonusTotal;
            const items = [
              { label: "Total possible", value: total, bold: true },
              ...(isGroupKnockout
                ? [{ label: "Group stage", value: groupTotal }]
                : []),
              { label: "Knockout stage", value: knockoutRoundsTotal + goalCountTotal },
              { label: "Bonus points", value: bonusTotal },
            ];
            return items.map((item) => (
              <div
                key={item.label}
                className={`rounded border border-border bg-background p-2 text-center ${item.bold ? "ring-1 ring-primary/20" : ""}`}
              >
                <div className="text-xs text-muted-foreground">{item.label}</div>
                <div className={`font-mono font-semibold ${item.bold ? "text-primary" : "text-foreground"}`}>
                  {item.value}
                </div>
              </div>
            ));
          })()}
        </div>
      </div>

      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override locked scoring config</DialogTitle>
            <DialogDescription>
              This league's scoring is locked because results have been entered.
              Changing it now will retroactively rescore every entry. Existing
              leaderboards will shift.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded border border-border bg-muted/40 p-3">
              <div className="text-xs font-semibold text-muted-foreground mb-2">
                Changes
              </div>
              {diff.length === 0 ? (
                <div className="text-xs text-muted-foreground">No changes.</div>
              ) : (
                <ul className="space-y-1 font-mono text-xs">
                  {diff.map((f) => (
                    <li key={f.key}>
                      {f.label}:{" "}
                      <span className="text-muted-foreground line-through">
                        {config[f.key] as number}
                      </span>{" "}
                      → <span className="text-foreground">{values[f.patchKey]}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Label className="text-xs">
                Type the league name exactly to confirm:{" "}
                <span className="font-mono text-foreground">{leagueName}</span>
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={leagueName}
                className="mt-1"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!confirmMatches || diff.length === 0}
              onClick={async () => {
                await onSave({ ...values, confirmLeagueName: confirmText });
                setOverrideOpen(false);
              }}
            >
              Override & save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============= Bonus questions card =============
type BonusKind = "admin_answered" | "over_under_int" | "over_under_count";

type BonusQuestion = {
  id: string;
  question_key: string;
  question_label: string;
  short_label: string | null;
  points: number;
  is_enabled: boolean;
  display_order: number;
  kind: BonusKind;
  threshold: number | null;
  derived_from_stat: string | null;
  over_label: string | null;
  under_label: string | null;
};

type BonusPatch = {
  points?: number;
  isEnabled?: boolean;
  questionLabel?: string;
  shortLabel?: string | null;
  threshold?: number | null;
  overLabel?: string;
  underLabel?: string;
};

function BonusQuestionsCard({
  questions,
  isLocked,
  onUpdate,
}: {
  questions: BonusQuestion[];
  isLocked: boolean;
  onUpdate: (id: string, patch: BonusPatch) => Promise<void>;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-2 sm:p-3 divide-y divide-border">
      {questions.map((q) => (
        <BonusQuestionRow
          key={q.id}
          question={q}
          isLocked={isLocked}
          onUpdate={onUpdate}
        />
      ))}
    </div>
  );
}

function BonusQuestionRow({
  question,
  isLocked,
  onUpdate,
}: {
  question: BonusQuestion;
  isLocked: boolean;
  onUpdate: (id: string, patch: BonusPatch) => Promise<void>;
}) {
  const [threshold, setThreshold] = useState<number>(question.threshold ?? 0);
  const [shortLabel, setShortLabel] = useState<string>(
    question.short_label ?? "",
  );
  const isOverUnder = question.kind !== "admin_answered";

  const dirty =
    (isOverUnder && threshold !== (question.threshold ?? 0)) ||
    shortLabel.trim() !== (question.short_label ?? "");

  const saveAll = async () => {
    const patch: BonusPatch = {};
    if (isOverUnder && threshold !== (question.threshold ?? 0)) {
      patch.threshold = threshold;
    }
    const trimmedShort = shortLabel.trim();
    if (trimmedShort !== (question.short_label ?? "")) {
      patch.shortLabel = trimmedShort.length > 0 ? trimmedShort : null;
    }
    await onUpdate(question.id, patch);
  };

  return (
    <div className="py-3 px-1 sm:px-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        <Switch
          checked={question.is_enabled}
          disabled={isLocked}
          onCheckedChange={(checked) => onUpdate(question.id, { isEnabled: checked })}
        />
        <div className="flex-1 min-w-0">
          <div
            className={cn(
              "text-sm font-medium",
              !question.is_enabled && "text-muted-foreground line-through",
            )}
          >
            {question.question_label}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {question.kind === "admin_answered" ? (
              <span>Admin-answered after tournament</span>
            ) : (
              <span>
                Live tracked: {question.derived_from_stat?.replace(/_/g, " ")}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
              Short
            </Label>
            <Input
              type="text"
              maxLength={40}
              disabled={isLocked || !question.is_enabled}
              value={shortLabel}
              onChange={(e) => setShortLabel(e.target.value)}
              placeholder={question.question_label}
              className="h-7 text-xs max-w-[200px]"
              title="Short label used in compact views like the leaderboard table. Leave blank to use the full question label."
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-1.5 sm:shrink-0 flex-wrap sm:flex-nowrap">
        {isOverUnder && (
          <div className="flex items-center gap-1.5">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Line
            </Label>
            <Input
              type="number"
              min={0}
              disabled={isLocked || !question.is_enabled}
              value={threshold}
              onChange={(e) =>
                setThreshold(Math.max(0, parseInt(e.target.value, 10) || 0))
              }
              className="w-20 font-mono text-right h-8"
              title="Threshold (over/under line)"
            />
          </div>
        )}
        <div
          className="flex items-center gap-1 rounded border border-border bg-muted/40 px-2 h-8"
          title="All bonus questions in this league are worth the same number of points. Edit the value in the Scoring → Extras section."
        >
          <span className="font-mono text-sm tabular-nums">+{question.points}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">pts</span>
        </div>
        {dirty && !isLocked && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={saveAll}
          >
            <Save className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
